import React, { useState } from 'react';
import { PDFDocument } from 'pdf-lib';

export const CropPdfView: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [margin, setMargin] = useState<number>(30);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCrop = async () => {
    if (!file) {
      setError("Please select a PDF file.");
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      
      const pages = pdf.getPages();
      pages.forEach((page) => {
        const { width, height } = page.getSize();
        // Crop margin from all sides
        page.setCropBox(margin, margin, width - (margin * 2), height - (margin * 2));
      });
      
      const pdfBytes = await pdf.save();
      const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cropped_${file.name}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      if (err instanceof Error && err.message.includes('encrypted')) {
        setError("This file is encrypted. Please provide a decrypted file.");
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Crop PDF</h2>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--color-text-secondary)' }}>Trim margins and borders uniformly from all pages.</p>
        </div>
        <button onClick={() => { void handleCrop(); }} disabled={isProcessing} style={{ padding: '0.5rem 1rem', background: 'var(--color-accent)', color: 'var(--color-on-accent)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>
          {isProcessing ? 'Cropping...' : 'Crop Margins'}
        </button>
      </div>
      {error && <div style={{ color: 'red', marginBottom: '1rem', padding: '0.5rem', background: '#ffebee', borderRadius: '4px' }}>{error}</div>}
      
      <div style={{ flex: 1, border: '2px dashed var(--color-border)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <input 
          type="file" 
          accept="application/pdf" 
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setFile(file);
              setError(null);
            }
          }}
          style={{ marginBottom: '1rem' }}
        />
        {file && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
            <span>{file.name}</span>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1rem' }}>
              <label htmlFor="marginInput">Margin size (points):</label>
              <input 
                id="marginInput"
                type="number" 
                min="0"
                value={margin}
                onChange={(e) => { setMargin(Number(e.target.value)); }}
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', width: '80px' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
